import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Reveal } from "../components/Reveal";
import { api, formatEUR } from "../lib/api";

const PageShell = ({ eyebrow, title, children, testid }) => (
  <main className="pt-28 sm:pt-36 pb-24 px-4 sm:px-8 lg:px-16 min-h-screen" data-testid={testid}>
    <div className="max-w-3xl">
      <Reveal>
        <p className="font-mono text-xs uppercase tracking-[0.3em] text-neutral-500">{eyebrow}</p>
        <h1 className="mt-3 font-display font-extrabold uppercase tracking-tighter text-4xl sm:text-6xl text-white">{title}</h1>
      </Reveal>
      <div className="mt-12 space-y-10">{children}</div>
    </div>
  </main>
);

const Block = ({ h, children }) => (
  <Reveal>
    <section>
      <h2 className="font-display font-bold uppercase tracking-wider text-xl text-white">{h}</h2>
      <div className="mt-4 text-sm sm:text-base text-neutral-400 leading-relaxed space-y-3">{children}</div>
    </section>
  </Reveal>
);

export const Delivery = () => {
  const [settings, setSettings] = useState(null);
  useEffect(() => { api.get("/settings").then(setSettings).catch(() => {}); }, []);
  const sh = settings?.shipping || {};

  return (
    <PageShell eyebrow="Informations" title="Livraison" testid="delivery-page">
      <Block h="Zones & frais">
        <p>Les frais de livraison sont calculés selon votre pays de livraison, au moment du checkout :</p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-px bg-line border border-line mt-4" data-testid="delivery-fees-table">
          {[
            { z: "France", k: "france" },
            { z: "Europe", k: "europe" },
            { z: "International", k: "international" },
          ].map(({ z, k }) => (
            <div key={k} className="bg-coal p-6">
              <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-neutral-500">{z}</p>
              <p className="mt-2 font-mono text-xl text-white" data-testid={`delivery-fee-${k}`}>
                {sh[k] !== undefined ? formatEUR(sh[k]) : "—"}
              </p>
            </div>
          ))}
        </div>
        {sh.free_threshold > 0 && (
          <p className="mt-4 font-mono text-[11px] uppercase tracking-[0.18em] text-gold">
            Livraison offerte dès {formatEUR(sh.free_threshold)} d'achat
          </p>
        )}
      </Block>
      <Block h="Délais d'expédition">
        <p>Commandes préparées sous 24 à 48 heures ouvrées. Comptez ensuite environ 2 à 4 jours ouvrés pour la France, 3 à 7 pour l'Europe et 5 à 12 jours pour l'international.</p>
        <p>Un lien de suivi vous est communiqué dès l'expédition de votre colis.</p>
      </Block>
      <Block h="Adresse de livraison">
        <p>Vérifiez soigneusement votre adresse lors du checkout : les colis sont expédiés à l'adresse saisie. Une erreur ? Contactez-nous au plus vite, tant que la commande n'est pas expédiée.</p>
      </Block>
    </PageShell>
  );
};

export const Returns = () => (
  <PageShell eyebrow="Informations" title="Retours" testid="returns-page">
    <Block h="Droit de rétractation">
      <p>Conformément à la réglementation, vous disposez de 14 jours à compter de la réception de votre commande pour exercer votre droit de rétractation, sans avoir à vous justifier.</p>
    </Block>
    <Block h="Conditions">
      <p>Les pièces doivent être neuves, non portées, non lavées, avec leurs étiquettes d'origine attachées. Les articles personnalisés ou en solde marqué « définitif » ne sont pas repris.</p>
    </Block>
    <Block h="Procédure">
      <p>1. Prévenez-nous via la page <Link to="/contact" className="text-white underline underline-offset-4 hover:text-gold">Contact</Link> en indiquant votre numéro de commande.</p>
      <p>2. Renvoyez les articles à l'adresse qui vous sera communiquée (les frais de retour restent à votre charge, sauf article défectueux).</p>
      <p>3. Après contrôle, le remboursement est effectué sous 14 jours via Stripe, sur votre moyen de paiement d'origine.</p>
    </Block>
    <Block h="Article défectueux ?">
      <p>Un défaut de fabrication ? Envoyez-nous une photo et votre numéro de commande : nous remplaçons ou remboursons la pièce, frais compris.</p>
    </Block>
  </PageShell>
);

export const Legal = () => (
  <PageShell eyebrow="Informations légales" title="Mentions légales" testid="legal-page">
    <Block h="Éditeur du site">
      <p>Le site moha66 (boutique en ligne) est édité et exploité par l'enseigne Moha66.</p>
      <p className="text-neutral-500">[Raison sociale complète] · [Forme juridique] · [Adresse du siège] · [SIRET] · [N° TVA intracommunautaire]</p>
      <p>Directeur de la publication : [Nom du responsable]. Contact : via la page <Link to="/contact" className="text-white underline underline-offset-4 hover:text-gold">Contact</Link>.</p>
    </Block>
    <Block h="Hébergement">
      <p>Ce site est hébergé sur l'infrastructure cloud Emergent. [Coordonnées complètes de l'hébergeur à compléter].</p>
    </Block>
    <Block h="Paiement">
      <p>Les paiements sont traités par Stripe Payments Europe. Aucune donnée bancaire ne transite ni n'est stockée sur nos serveurs.</p>
    </Block>
    <Block h="Propriété intellectuelle">
      <p>L'ensemble des contenus (textes, visuels, logo et artwork Moha66) est protégé. Toute reproduction sans autorisation écrite est interdite.</p>
    </Block>
    <Block h="Consommation">
      <p>Les produits sont vendus en EUR, TVA incluse. Conformément au Code de la consommation, le client dispose d'un droit de rétractation de 14 jours (voir page Retours).</p>
    </Block>
  </PageShell>
);

export const Privacy = () => (
  <PageShell eyebrow="Confidentialité" title="Politique de confidentialité" testid="privacy-page">
    <Block h="Données collectées">
      <p>Nous collectons uniquement les données nécessaires au traitement de votre commande : nom, email, téléphone (facultatif) et adresse de livraison. Les messages envoyés via la page Contact nous sont également transmis.</p>
    </Block>
    <Block h="Données bancaires — jamais stockées">
      <p>Vos paiements sont traités intégralement par Stripe Checkout. <span className="text-white">Moha66 ne voit jamais et ne stocke jamais vos données bancaires</span> : elles sont chiffrées et traitées par Stripe seul.</p>
    </Block>
    <Block h="Utilisation">
      <p>Vos données servent à : traiter et expédier vos commandes, vous envoyer la confirmation de commande, répondre à vos demandes. Elles ne sont ni vendues, ni louées, ni transmises à des tiers à des fins commerciales.</p>
    </Block>
    <Block h="Conservation & sécurité">
      <p>Les données de commande sont conservées pour la durée légale applicable. L'accès à notre espace d'administration est protégé et réservé aux équipes autorisées.</p>
    </Block>
    <Block h="Vos droits (RGPD)">
      <p>Accès, rectification, effacement, portabilité, opposition : écrivez-nous via la page <Link to="/contact" className="text-white underline underline-offset-4 hover:text-gold">Contact</Link>. Vous pouvez également saisir la CNIL.</p>
    </Block>
    <Block h="Cookies">
      <p>Le site utilise uniquement un stockage local technique pour mémoriser votre panier. Aucun cookie publicitaire ni traceur tiers.</p>
    </Block>
  </PageShell>
);
